import React from "react";
import { Plus, Trash2 } from "lucide-react";

let _cid = 0;
const _tempId = (prefix) => `_tmp_${prefix}_${++_cid}`;

export default function ProdutoFormAbaComplementos({ data, update }) {
  const grupos = data.grupos_complementos || [];

  const setGrupo = (idx, patch) => {
    const next = grupos.map((g, i) => (i === idx ? { ...g, ...patch } : g));
    update({ grupos_complementos: next });
  };

  const addGrupo = () => {
    update({
      grupos_complementos: [
        ...grupos,
        {
          id: _tempId("grp"),
          nome: "Novo grupo",
          obrigatorio: false,
          minimo: null,
          maximo: 1,
          opcoes: [],
        },
      ],
    });
  };

  const delGrupo = (idx) => {
    update({ grupos_complementos: grupos.filter((_, i) => i !== idx) });
  };

  const setOpcao = (gIdx, oIdx, patch) => {
    const next = grupos.map((g, i) => {
      if (i !== gIdx) return g;
      const opcoes = (g.opcoes || []).map((o, j) => (j === oIdx ? { ...o, ...patch } : o));
      return { ...g, opcoes };
    });
    update({ grupos_complementos: next });
  };

  const addOpcao = (gIdx) => {
    const next = grupos.map((g, i) =>
      i === gIdx
        ? {
            ...g,
            opcoes: [
              ...(g.opcoes || []),
              { id: _tempId("opt"), nome: "", preco_adicional: 0, disponivel: true },
            ],
          }
        : g,
    );
    update({ grupos_complementos: next });
  };

  const delOpcao = (gIdx, oIdx) => {
    const next = grupos.map((g, i) =>
      i === gIdx ? { ...g, opcoes: (g.opcoes || []).filter((_, j) => j !== oIdx) } : g,
    );
    update({ grupos_complementos: next });
  };

  return (
    <div className="space-y-3" data-testid="aba-complementos">
      <div className="flex items-center justify-between">
        <p className="text-xs text-[var(--text-secondary)]">
          Crie grupos como "Recheio", "Tamanho", "Adicionais" para o cliente customizar.
        </p>
        <button
          type="button"
          onClick={addGrupo}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-orange-500 text-white rounded-lg hover:bg-orange-600"
          data-testid="add-grupo-complemento"
        >
          <Plus className="w-3.5 h-3.5" /> Adicionar grupo
        </button>
      </div>

      {grupos.length === 0 && (
        <div className="text-xs text-[var(--text-secondary)] bg-[var(--bg-secondary)] rounded p-3 text-center">
          Nenhum grupo de complementos. Produtos como bolos, pizzas e marmitas costumam ter.
        </div>
      )}

      {grupos.map((g, gIdx) => (
        <div
          key={g.id || gIdx}
          className="border border-[var(--border)] rounded-lg p-3 space-y-2 bg-white"
          data-testid={`grupo-complemento-${gIdx}`}
        >
          <div className="flex items-start gap-2">
            <input
              type="text"
              value={g.nome}
              onChange={(e) => setGrupo(gIdx, { nome: e.target.value })}
              className="flex-1 px-2 py-1.5 border border-[var(--border)] rounded text-sm font-semibold"
              placeholder="Nome do grupo"
              maxLength={50}
            />
            <button
              type="button"
              onClick={() => delGrupo(gIdx)}
              className="p-1.5 text-red-600 hover:bg-red-50 rounded"
              title="Remover grupo"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <label className="flex items-center gap-1.5">
              <input
                type="checkbox"
                checked={!!g.obrigatorio}
                onChange={(e) =>
                  setGrupo(gIdx, {
                    obrigatorio: e.target.checked,
                    minimo: e.target.checked ? Math.max(1, g.minimo || 1) : null,
                  })
                }
              />
              Obrigatório
            </label>
            {g.obrigatorio && (
              <label className="flex items-center gap-1.5">
                Mín:
                <input
                  type="number"
                  min={1}
                  value={g.minimo || 1}
                  onChange={(e) => setGrupo(gIdx, { minimo: Math.max(1, parseInt(e.target.value || 1, 10)) })}
                  className="w-14 px-1.5 py-1 border border-[var(--border)] rounded"
                />
              </label>
            )}
            <label className="flex items-center gap-1.5">
              Máx:
              <input
                type="number"
                min={1}
                value={g.maximo || 1}
                onChange={(e) => setGrupo(gIdx, { maximo: Math.max(1, parseInt(e.target.value || 1, 10)) })}
                className="w-14 px-1.5 py-1 border border-[var(--border)] rounded"
              />
            </label>
          </div>

          {/* Opções */}
          <div className="space-y-1.5">
            {(g.opcoes || []).map((o, oIdx) => (
              <div key={o.id || oIdx} className="flex items-center gap-1.5">
                <input
                  type="text"
                  value={o.nome}
                  onChange={(e) => setOpcao(gIdx, oIdx, { nome: e.target.value })}
                  placeholder="Nome da opção"
                  maxLength={50}
                  className="flex-1 px-2 py-1.5 border border-[var(--border)] rounded text-sm"
                />
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={o.preco_adicional}
                  onChange={(e) => setOpcao(gIdx, oIdx, { preco_adicional: Math.max(0, parseFloat(e.target.value || 0)) })}
                  placeholder="+R$"
                  className="w-20 px-1.5 py-1.5 border border-[var(--border)] rounded text-xs"
                  title="Preço adicional"
                />
                <label className="flex items-center" title="Disponível">
                  <input
                    type="checkbox"
                    checked={o.disponivel !== false}
                    onChange={(e) => setOpcao(gIdx, oIdx, { disponivel: e.target.checked })}
                  />
                </label>
                <button
                  type="button"
                  onClick={() => delOpcao(gIdx, oIdx)}
                  className="p-1 text-red-600 hover:bg-red-50 rounded"
                  title="Remover opção"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => addOpcao(gIdx)}
              className="inline-flex items-center gap-1 text-xs text-orange-600 hover:text-orange-700"
              data-testid={`add-opcao-${gIdx}`}
            >
              <Plus className="w-3 h-3" /> Adicionar opção
            </button>
          </div>

          {g.obrigatorio && (g.opcoes || []).length < 2 && (
            <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
              Grupo obrigatório deve ter pelo menos 2 opções para o cliente escolher.
            </p>
          )}
        </div>
      ))}
    </div>
  );
}
